// src/services/MockDataService.ts

// Types matching your database models
export interface Customer {
  phone_number: string;
  first_seen_at: string;
  initial_channel: string;
  conversation_ids: string[];
  active_ticket_ids: string[];
  last_active_ticket_id?: string;
  sms_opted_in: boolean;
  sms_opted_in_at?: string;
  customer_status: 'pending_consent' | 'pending_terms' | 'active' | 'unsubscribed';
  terms_accepted: boolean;
  terms_version: string;
  terms_accepted_at?: string;
  extracted_info: Record<string, string>;
}

export interface Ticket {
  ticket_id: string;
  service_type: string;
  issue_description: string;
  conversation_id: string;
  phone_number: string;
  status: 'draft' | 'open' | 'in_progress' | 'fulfilled' | 'cancelled';
  urgency_level?: boolean;
  extracted_fields: Record<string, string>;
  summary_text: string;
  created_at: string;
  completion_percentage: number;
  selected_provider_id?: string;
  llm_ranked_providers: any[];
}

export interface ConversationMessage {
  conversation_id: string;
  message_id: string;
  role: 'user' | 'Lookey' | 'system' | 'provider';
  content: string;
  message_type: 'text' | 'action' | 'summary';
  timestamp: string;
  from_number?: string;
  metadata: Record<string, any>;
}

export interface ApplicationEvent {
  phone_number: string;
  event_timestamp: string;
  event_type: string;
  status: 'success' | 'failed' | 'pending' | 'in_progress';
  user_type: 'customer' | 'provider' | 'system';
  ticket_id?: string;
  provider_id?: string;
  created_at: string;
  metadata: Record<string, any>;
}

class MockDataService {
  private customers: Customer[] = [];
  private tickets: Ticket[] = [];
  private messages: ConversationMessage[] = [];
  private events: ApplicationEvent[] = [];

  constructor() {
    this.generateMockData();
  }

  private generateMockData() {
    // Generate 50 realistic customers
    for (let i = 0; i < 50; i++) {
      const customer = this.createMockCustomer(i);
      this.customers.push(customer);
      
      // Generate 1-3 tickets per customer
      const ticketCount = Math.floor(Math.random() * 3) + 1;
      for (let j = 0; j < ticketCount; j++) {
        const ticket = this.createMockTicket(customer.phone_number);
        this.tickets.push(ticket);
        customer.active_ticket_ids.push(ticket.ticket_id);
        
        // Generate 5-15 messages per ticket
        const messageCount = Math.floor(Math.random() * 10) + 5;
        for (let k = 0; k < messageCount; k++) {
          const message = this.createMockMessage(ticket.conversation_id, k);
          this.messages.push(message);
        }
        
        // Generate 3-8 events per ticket
        const eventCount = Math.floor(Math.random() * 5) + 3;
        for (let e = 0; e < eventCount; e++) {
          const event = this.createMockEvent(customer.phone_number, ticket.ticket_id, e);
          this.events.push(event);
        }
      }
      
      if (customer.active_ticket_ids.length > 0) {
        customer.last_active_ticket_id = customer.active_ticket_ids[customer.active_ticket_ids.length - 1];
      }
    }

    console.log(`📊 Mock data generated: ${this.customers.length} customers, ${this.tickets.length} tickets, ${this.messages.length} messages, ${this.events.length} events`);
  }

  private createMockCustomer(index: number): Customer {
    const phoneNumbers = [
      '+1234567890', '+1987654321', '+1555123456', '+1444987654', '+1333567890',
      '+1222345678', '+1111987654', '+1999123456', '+1888567890', '+1777234567',
      '+1666789012', '+1555987654', '+1444123456', '+1333789012', '+1222567890',
      '+1214555123', '+1469987456', '+1972234567', '+1817654321', '+1940123789',
      '+1903456789', '+1430987654', '+1512345678', '+1979123456', '+1281567890',
      '+1713234567', '+1832789012', '+1346987654', '+1409123456', '+1936567890',
      '+1254789012', '+1361234567', '+1915987654', '+1432123456', '+1325789012',
      '+1806567890', '+1940234567', '+1903789012', '+1737987654', '+1214123456',
      '+1469789012', '+1972567890', '+1817234567', '+1940987654', '+1903123456',
      '+1430789012', '+1512567890', '+1979234567', '+1281987654', '+1713123456'
    ];

    const statuses: Customer['customer_status'][] = ['active', 'active', 'active', 'pending_terms', 'unsubscribed'];
    const channels = ['sms', 'web', 'sms', 'sms'];
    
    const now = new Date();
    const createdAt = new Date(now.getTime() - Math.random() * 30 * 24 * 60 * 60 * 1000); // Last 30 days
    
    return {
      phone_number: phoneNumbers[index] || `+1${Math.floor(Math.random() * 9000000000) + 1000000000}`,
      first_seen_at: createdAt.toISOString(),
      initial_channel: channels[Math.floor(Math.random() * channels.length)],
      conversation_ids: [],
      active_ticket_ids: [],
      sms_opted_in: Math.random() > 0.1, // 90% opted in
      sms_opted_in_at: Math.random() > 0.1 ? createdAt.toISOString() : undefined,
      customer_status: statuses[Math.floor(Math.random() * statuses.length)],
      terms_accepted: Math.random() > 0.2, // 80% accepted
      terms_version: 'v1.0',
      terms_accepted_at: Math.random() > 0.2 ? createdAt.toISOString() : undefined,
      extracted_info: {
        preferred_contact_time: ['morning', 'afternoon', 'evening'][Math.floor(Math.random() * 3)],
        zip_code: ['75001', '75002', '75003', '75024', '75025'][Math.floor(Math.random() * 5)]
      }
    };
  }

  private createMockTicket(phoneNumber: string): Ticket {
    const serviceTypes = [
      'plumbing', 'house_cleaning', 'lawn_care', 'hvac_repair', 'electrical_work',
      'handyman_services', 'pest_control', 'appliance_repair', 'painting', 'carpet_cleaning'
    ];
    
    const statuses: Ticket['status'][] = ['open', 'in_progress', 'fulfilled', 'cancelled', 'draft'];
    const issues = [
      'Leaky faucet in kitchen needs repair',
      'Weekly house cleaning service needed',
      'Lawn mowing and trimming required',
      'AC unit not cooling properly',
      'Electrical outlet not working',
      'Multiple small repairs around the house',
      'Ant infestation in kitchen',
      'Dishwasher making strange noises',
      'Interior walls need painting',
      'Carpet stain removal needed'
    ];

    const ticketId = `TICKET_${Math.random().toString(36).substr(2, 8).toUpperCase()}`;
    const conversationId = `conv_${Math.random().toString(36).substr(2, 16)}`;
    const serviceType = serviceTypes[Math.floor(Math.random() * serviceTypes.length)];
    
    const createdAt = new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000); // Last 7 days

    return {
      ticket_id: ticketId,
      service_type: serviceType,
      issue_description: issues[Math.floor(Math.random() * issues.length)],
      conversation_id: conversationId,
      phone_number: phoneNumber,
      status: statuses[Math.floor(Math.random() * statuses.length)],
      urgency_level: Math.random() > 0.7, // 30% urgent
      extracted_fields: {
        address: ['123 Main St', '456 Oak Ave', '789 Pine Rd'][Math.floor(Math.random() * 3)],
        preferred_time: ['morning', 'afternoon', 'evening'][Math.floor(Math.random() * 3)],
        budget_range: ['$50-100', '$100-200', '$200+'][Math.floor(Math.random() * 3)]
      },
      summary_text: `${serviceType.replace('_', ' ')} service request`,
      created_at: createdAt.toISOString(),
      completion_percentage: Math.floor(Math.random() * 100),
      selected_provider_id: Math.random() > 0.5 ? `provider_${Math.random().toString(36).substr(2, 8)}` : undefined,
      llm_ranked_providers: []
    };
  }

  private createMockMessage(conversationId: string, index: number): ConversationMessage {
    const roles: ConversationMessage['role'][] = ['user', 'Lookey', 'user', 'Lookey'];
    const userMessages = [
      "I need help with a plumbing issue",
      "My faucet is leaking in the kitchen",
      "Yes, I'm available this afternoon",
      "That sounds good, please proceed",
      "How much will this cost?",
      "I'm located in McKinney, TX"
    ];
    
    const lookeyMessages = [
      "I'd be happy to help you find a plumber! Can you tell me more about the issue?",
      "I understand you have a leaky faucet. What's your address so I can find nearby providers?",
      "Great! I'm reaching out to qualified plumbers in your area now.",
      "I found 3 excellent plumbers. Here are their details...",
      "The typical cost for this service ranges from $75-150. Would you like to proceed?",
      "Perfect! I'm creating your service request now."
    ];

    const role = roles[index % roles.length];
    const messages = role === 'user' ? userMessages : lookeyMessages;
    
    const baseTime = new Date(Date.now() - Math.random() * 24 * 60 * 60 * 1000); // Last 24 hours
    const messageTime = new Date(baseTime.getTime() + index * 2 * 60 * 1000); // 2 minutes apart

    return {
      conversation_id: conversationId,
      message_id: `msg_${Math.random().toString(36).substr(2, 16)}`,
      role,
      content: messages[Math.floor(Math.random() * messages.length)],
      message_type: 'text' as const,
      timestamp: messageTime.toISOString(),
      from_number: role === 'user' ? '+1234567890' : undefined,
      metadata: {
        source: 'sms',
        processed: true
      }
    };
  }

  private createMockEvent(phoneNumber: string, ticketId: string, index: number): ApplicationEvent {
    const eventTypes = [
      'customer_inquiry_started',
      'ticket_created',
      'providers_contacted',
      'job_offer_received',
      'job_accepted',
      'payment_required',
      'payment_completed',
      'service_completed'
    ];

    const statuses: ApplicationEvent['status'][] = ['success', 'success', 'failed', 'pending'];
    const userTypes: ApplicationEvent['user_type'][] = ['customer', 'system', 'provider'];

    const baseTime = new Date(Date.now() - Math.random() * 24 * 60 * 60 * 1000);
    const eventTime = new Date(baseTime.getTime() + index * 5 * 60 * 1000); // 5 minutes apart

    return {
      phone_number: phoneNumber,
      event_timestamp: `${eventTime.toISOString()}_${Math.random().toString(36).substr(2, 8)}`,
      event_type: eventTypes[Math.floor(Math.random() * eventTypes.length)],
      status: statuses[Math.floor(Math.random() * statuses.length)],
      user_type: userTypes[Math.floor(Math.random() * userTypes.length)],
      ticket_id: ticketId,
      provider_id: Math.random() > 0.5 ? `provider_${Math.random().toString(36).substr(2, 8)}` : undefined,
      created_at: eventTime.toISOString(),
      metadata: {
        source: 'system',
        details: 'Generated mock event'
      }
    };
  }

  // API Methods
  async searchCustomers(query: string): Promise<Customer[]> {
    await this.delay(200); // Simulate API delay
    
    if (!query.trim()) return this.customers.slice(0, 20);
    
    const filtered = this.customers.filter(customer => 
      customer.phone_number.includes(query) ||
      customer.customer_status.includes(query.toLowerCase())
    );
    
    return filtered.slice(0, 20);
  }

  async getCustomer(phoneNumber: string): Promise<Customer | null> {
    await this.delay(150);
    return this.customers.find(c => c.phone_number === phoneNumber) || null;
  }

  async getCustomerTickets(phoneNumber: string): Promise<Ticket[]> {
    await this.delay(100);
    return this.tickets.filter(t => t.phone_number === phoneNumber);
  }

  async getTicket(ticketId: string): Promise<Ticket | null> {
    await this.delay(100);
    return this.tickets.find(t => t.ticket_id === ticketId) || null;
  }

  async getConversationMessages(conversationId: string): Promise<ConversationMessage[]> {
    await this.delay(150);
    return this.messages
      .filter(m => m.conversation_id === conversationId)
      .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
  }

  async getEvents(phoneNumber?: string, limit = 100): Promise<ApplicationEvent[]> {
    await this.delay(200);
    
    let filtered = this.events;
    if (phoneNumber) {
      filtered = filtered.filter(e => e.phone_number === phoneNumber);
    }
    
    return filtered
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
      .slice(0, limit);
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

// Export singleton instance
export const mockDataService = new MockDataService();